import { PaperTooltip } from '@/components/PaperTooltip';
import { Bolt, Info, Paid } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import {
  formattedDecimal,
  formattedPercent,
  formattedUSD,
} from '../../utils.ts';
import { SystemCardPieChart } from './SystemCardPieChart.tsx';

type Props = {
  energyDemand?: number;
  capitalCost?: number;
  systemCoverageName: string;
  systemCoverageHelperText: string;
  systemCoverageIcon: React.ReactElement;
  coveragePercentage?: number;
  /**
   * Optional explanation of where the capital cost figure comes from, shown in
   * the CAPITAL COST tooltip. Set by categories whose total is not simply
   * per-unit cost x units (e.g. HVAC with a passive measure selected).
   */
  capitalCostHelperText?: React.ReactNode;
};

export const DecarbOverviewHeader: React.FC<Props> = ({
  energyDemand,
  capitalCost,
  systemCoverageName,
  systemCoverageHelperText,
  systemCoverageIcon,
  coveragePercentage,
  capitalCostHelperText,
}) => {
  return (
    <Stack direction={'row'} spacing={0.5} flex={1} sx={{ overflow: 'scroll' }}>
      <Stack className={'overview-header-gradient'} flex={1} py={2} px={4}>
        <Stack>
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            minHeight={30}
          >
            <Bolt fontSize={'small'} color={'info'} />
            <Typography
              variant={'subtitle2'}
              fontSize={{ sm: 4, md: 6, lg: 9, xl: 12 }}
            >
              ENERGY DEMAND
            </Typography>
            <PaperTooltip
              title={
                <Stack spacing={0}>
                  <Typography fontWeight={'bold'}>Energy Demand</Typography>
                  <Typography>
                    shows how much energy it will take to power all of your
                    selections for a year. Keep this number low to decrease
                    greenhouse gas emissions
                  </Typography>
                </Stack>
              }
            >
              <Info color={'success'} fontSize={'small'} />
            </PaperTooltip>
          </Stack>
          <Box>
            <Typography
              fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
              variant={'subtitle2'}
            >
              {energyDemand !== undefined
                ? `${formattedDecimal(energyDemand)} MWH`
                : '--'}
            </Typography>
          </Box>{' '}
        </Stack>
      </Stack>
      <Stack className={'overview-header-gradient'} flex={1} py={2} px={4}>
        <Stack>
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            minHeight={30}
          >
            <Paid fontSize={'small'} color={'info'} />
            <Typography
              variant={'subtitle2'}
              fontSize={{ sm: 4, md: 6, lg: 9, xl: 12 }}
            >
              CAPITAL COST
            </Typography>
            <PaperTooltip
              title={
                <Stack spacing={0}>
                  <Typography fontWeight={'bold'}>CAPITAL COST</Typography>
                  {capitalCostHelperText !== undefined && (
                    <Typography>{capitalCostHelperText}</Typography>
                  )}
                  {/* // TODO determine hover state text of 'capital cost'
                  <Typography>
                    shows how much CO2 will be produced by your selected energy
                    producers over the course of the year in order to meet your
                    energy needs.
                  </Typography> */}
                </Stack>
              }
            >
              <Info color={'success'} fontSize={'small'} />
            </PaperTooltip>
          </Stack>
          <Box>
            <Typography
              fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
              variant={'subtitle2'}
            >
              {capitalCost !== undefined ? formattedUSD(capitalCost) : '--'}
            </Typography>
          </Box>{' '}
        </Stack>
      </Stack>
      <Stack className={'overview-header-gradient'} flex={1} py={2} px={4}>
        <Stack>
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            minHeight={30}
          >
            {/** clone the icon to force it to have the given props */}
            {React.cloneElement(systemCoverageIcon, {
              fontSize: 'small',
              color: 'info',
            })}
            <Typography
              variant={'subtitle2'}
              fontSize={{ sm: 4, md: 6, lg: 9, xl: 12 }}
              sx={{ textTransform: 'uppercase' }}
            >
              {systemCoverageName}
            </Typography>
            <PaperTooltip
              title={
                <Stack spacing={0}>
                  <Typography fontWeight={'bold'}>
                    {systemCoverageName}
                  </Typography>
                  <Typography>{systemCoverageHelperText}</Typography>
                </Stack>
              }
            >
              <Info color={'success'} fontSize={'small'} />
            </PaperTooltip>
          </Stack>
          <Box>
            <Typography
              fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
              variant={'subtitle2'}
            >
              {coveragePercentage !== undefined ? (
                <Stack direction={'row'} alignItems={'center'}>
                  <SystemCardPieChart
                    width={30}
                    coverage={coveragePercentage}
                  />
                  <Typography
                    variant={'subtitle2'}
                    fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
                  >
                    {formattedPercent(coveragePercentage)}
                  </Typography>
                </Stack>
              ) : (
                '--'
              )}
            </Typography>
          </Box>{' '}
        </Stack>
      </Stack>
    </Stack>
  );
};
